import { ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { memo, useCallback, useContext, useMemo } from 'react';
import { SelectItemContent } from '../../../../components/selects/SelectItem';
import { isAnnotationContextGroup } from '../../../../contexts/AnnotationContext';
import { RendererContext } from '../../../../contexts/RendererContext';
import { useAnnotationTree, useAnnotationLookup } from '../../../../hooks/potree/useAnnotationContext';
import { useDeleteConfirmation, type DeletableType } from '../../../../contexts/DeleteConfirmationContext';
import { useDeleteAnnotation } from '../../../../hooks/potree/useDeleteAnnotation';
import { useCustomSelector } from '../../../../redux/store';
import { T, useT } from '../../../../translation/src';
import { GroupType } from '../../../../types/graphqlTypes';
import { useExportDXF } from '../../../../hooks/potree/useExportDXF';
import { useExportIFC } from '../../../../hooks/potree/useExportIFC';
import { useFlatAnnotationList } from '../../../../hooks/potree/useFlatAnnotationList';
import { useToastNotifications, ToastType } from '../../../../hooks/useToastNotifications';
import { roomIdFromAutoFolderId } from '../../../../utils/roomFolders';
import { mergeRoomsInScene } from '../../../../potree/utils/mergeRooms';
import type { PointoramaRoom } from '../../../../potree/utils/PointoramaRoom';
import MergeRoomsIcon from '../../../../assets/icons/merge-rooms.svg?react';

const MultiSelectMenuItems_: React.FC2<{ children?: React.ReactNode }> = ({ children }) => {
  const { selectedAnnotations: selectedAnnotationsAndGroups } = useCustomSelector((state) => state.rendererProvider, ['selectedAnnotations']);
  const { viewer } = useContext(RendererContext);
  const { deleteSelected } = useDeleteAnnotation();
  const [exportDXF, isExportDXFPossible] = useExportDXF();
  const [exportIFC, isExportIFCPossible] = useExportIFC();
  const { allSelectedAnnotations } = useFlatAnnotationList();
  const { requestDeleteConfirmation, requiresConfirmation } = useDeleteConfirmation();
  const { showToast } = useToastNotifications();
  const notAdjacentTitle = useT('nothing merged', { swc: true });
  const notAdjacentText = useT('the selected rooms do not touch. only adjacent rooms can be merged', { swc: true });
  const annotationsFromContext = useAnnotationTree();
  const annotationsByIdentifier = useAnnotationLookup();

  // Rooms in the current selection: room annotations directly, room folders
  // via their id scheme (`roomgroup_<roomAnnotationId>`, see utils/roomFolders).
  const selectedRooms = useMemo(() => {
    if (!viewer) return [];
    const roomsById = new Map<string, PointoramaRoom>(viewer.scene.rooms.map((r) => [r.identifier, r]));
    const rooms: PointoramaRoom[] = [];
    const seen = new Set<string>();
    for (const selected of selectedAnnotationsAndGroups) {
      const roomId = selected.type === 'annotation' ? selected.identifier : roomIdFromAutoFolderId(selected.identifier);
      const room = roomId ? roomsById.get(roomId) : undefined;
      if (room && !seen.has(room.identifier)) {
        seen.add(room.identifier);
        rooms.push(room);
      }
    }
    return rooms;
  }, [viewer, selectedAnnotationsAndGroups]);

  const onMergeRooms = useCallback(() => {
    if (!viewer) return;
    const merged = mergeRoomsInScene(viewer, selectedRooms);
    if (merged.length === 0) {
      showToast({ title: notAdjacentTitle, text: notAdjacentText, type: ToastType.WARNING });
    }
  }, [viewer, selectedRooms, showToast, notAdjacentTitle, notAdjacentText]);

  const groupTypesByIdentifier = useMemo(() => {
    const map: Record<string, GroupType | undefined> = {};
    annotationsFromContext.forEach((item) => {
      if (isAnnotationContextGroup(item)) {
        map[item.identifier] = item.type;
      }
    });
    return map;
  }, [annotationsFromContext]);

  const onDeleteAnnotations = useCallback(() => {
    deleteSelected(selectedAnnotationsAndGroups);
  }, [deleteSelected, selectedAnnotationsAndGroups]);

  const onDeleteAnnotationsWithConfirm = useCallback(() => {
    const needsConfirmation = selectedAnnotationsAndGroups.some((selected) => {
      if (selected.type === 'group') {
        // Room folders may lack a populated `type` — their id scheme is
        // authoritative (`roomgroup_*`, see utils/roomFolders).
        const groupType = groupTypesByIdentifier[selected.identifier] ?? (selected.identifier.startsWith('roomgroup_') ? GroupType.Room : undefined);
        return requiresConfirmation(groupType);
      }
      const annotation = annotationsByIdentifier[selected.identifier];
      return requiresConfirmation(annotation?.type as DeletableType | undefined);
    });

    if (needsConfirmation) {
      requestDeleteConfirmation(onDeleteAnnotations);
    } else {
      onDeleteAnnotations();
    }
  }, [selectedAnnotationsAndGroups, groupTypesByIdentifier, annotationsByIdentifier, requiresConfirmation, requestDeleteConfirmation, onDeleteAnnotations]);

  const onExportAnnotationsDXF = useCallback(() => {
    exportDXF(allSelectedAnnotations);
  }, [exportDXF, allSelectedAnnotations]);

  const onExportAnnotationsIFC = useCallback(() => {
    exportIFC(allSelectedAnnotations);
  }, [exportIFC, allSelectedAnnotations]);

  const dxfExportPossible = useMemo(() => {
    return isExportDXFPossible(allSelectedAnnotations);
  }, [isExportDXFPossible, allSelectedAnnotations]);

  const ifcExportPossible = useMemo(() => {
    return isExportIFCPossible(allSelectedAnnotations);
  }, [isExportIFCPossible, allSelectedAnnotations]);

  return (
    <>
      {children}
      {selectedRooms.length >= 2 && (
        <div>
          <SelectItemContent icon={MergeRoomsIcon} onClick={onMergeRooms}>
            <T _str="merge rooms" swc />
          </SelectItemContent>
        </div>
      )}
      <div className="flex flex-col">
        <SelectItemContent icon={ArrowDownTrayIcon} onClick={onExportAnnotationsDXF} disabled={!dxfExportPossible}>
          <T _str="export to DXF" swc />
        </SelectItemContent>
        <SelectItemContent icon={ArrowDownTrayIcon} onClick={onExportAnnotationsIFC} disabled={!ifcExportPossible}>
          <T _str="export to IFC" swc />
        </SelectItemContent>
      </div>
      <div>
        <SelectItemContent icon={TrashIcon} onClick={onDeleteAnnotationsWithConfirm}>
          <T _str="delete" swc />
        </SelectItemContent>
      </div>
    </>
  );
};
export const MultiSelectMenuItems = memo(MultiSelectMenuItems_);
